import { ROTULO_SITUACAO_COBRANCA } from '@social/shared';
import type {
  Acesso,
  LimitesContratados,
  Periodicidade,
  SituacaoAssinatura,
  SituacaoCiclo,
  SituacaoCobranca,
  UsoAtual,
} from '@social/shared';

/** Espelha `PanoramaAssinatura` da API (`apps/api/src/assinaturas/panorama.ts`). */
export interface Panorama {
  assinatura: {
    id: string;
    situacao: SituacaoAssinatura;
    origem: string;
    inicio: string;
    fim: string;
    precoContratadoCentavos: number;
  } | null;
  plano: PlanoResumo | null;
  acesso: Acesso;
  limites: LimitesContratados;
  uso: UsoAtual;
  ciclos: Ciclo[];
  planosDisponiveis: PlanoResumo[];
  cobrancaEmAberto: Cobranca | null;
  faturamentoCompleto: boolean;
  faturamento: { cnpj: string | null; emailCobranca: string | null };
  pagamentoDisponivel: boolean;
}

/** Espelha `ResumoCobranca` da API. */
export interface Cobranca {
  id: string;
  meio: string;
  situacao: SituacaoCobranca;
  valorCentavos: number;
  venceEm: string | null;
  pagoEm: string | null;
  urlPagamento: string | null;
  pixCopiaECola: string | null;
}

export const ROTULO_COBRANCA = ROTULO_SITUACAO_COBRANCA;

export interface PlanoResumo {
  codigo: string;
  nome: string;
  periodicidade: Periodicidade;
  precoCentavos: number;
  limites: LimitesContratados;
}

export interface Ciclo {
  sequencia: number;
  inicio: string;
  fim: string;
  valorCentavos: number;
  situacao: SituacaoCiclo;
  venceEm: string;
  pagoEm: string | null;
}

/**
 * O período no NOME do plano, não no preço.
 *
 * Os três planos do catálogo se chamam "Essencial" — só a periodicidade os
 * distingue. Com o período embaixo, no preço, a tela mostrava três cards de
 * título idêntico lado a lado, e quem navega por leitor de tela ouvia
 * "Essencial, Essencial, Essencial". O período subiu para o título, e o preço
 * ficou só o valor.
 */
export const ROTULO_PERIODICIDADE: Record<Periodicidade, string> = {
  MENSAL: 'mensal',
  TRIMESTRAL: 'trimestral',
  ANUAL: 'anual',
};

export const ROTULO_SITUACAO: Record<SituacaoAssinatura, string> = {
  TRIAL: 'Em teste',
  ATIVA: 'Ativa',
  INADIMPLENTE: 'Pagamento em atraso',
  SUSPENSA: 'Suspensa',
  CANCELADA: 'Cancelada',
  EXPIRADA: 'Expirada',
};

export const ROTULO_CICLO: Record<SituacaoCiclo, string> = {
  PENDENTE: 'A acertar',
  PAGO: 'Pago',
  VENCIDO: 'Vencido',
  ISENTO: 'Isento',
  CANCELADO: 'Cancelado',
};

/**
 * O tom da situação — o que decide a cor da etiqueta.
 *
 * `TRIAL` é neutro, não `ok`: teste em andamento não é assinatura em dia, e
 * pintá-lo de verde faria o escritório achar que já contratou.
 */
export function tomDaSituacao(
  s: SituacaoAssinatura,
): 'neutro' | 'ok' | 'alerta' | 'erro' {
  if (s === 'ATIVA') return 'ok';
  if (s === 'TRIAL') return 'neutro';
  if (s === 'INADIMPLENTE') return 'alerta';
  return 'erro';
}

export interface AvisoDeEstado {
  tom: 'alerta' | 'erro';
  texto: string;
}

/**
 * O aviso do topo — ou `null`, quando não há o que avisar.
 *
 * Retornar `null` é a metade que importa: uma faixa permanente dizendo "tudo
 * certo" ocupa o lugar mais nobre da tela para não informar nada, e depois de
 * uma semana ninguém mais a lê — inclusive no dia em que ela mudar para o
 * aviso que importava.
 *
 * O limiar de 3 dias não é enfeite: é quanto tempo alguém precisa para falar
 * com quem decide a compra no escritório. Avisar no último dia é avisar tarde.
 */
export function avisoDeEstado(acesso: Acesso): AvisoDeEstado | null {
  if (!acesso.podeEscrever) {
    return {
      tom: 'erro',
      texto:
        acesso.motivo ??
        'O cadastro está bloqueado. Contrate um plano para voltar a trabalhar.',
    };
  }
  if (acesso.diasRestantes <= 3) {
    const dias = acesso.diasRestantes;
    const quando = dias <= 0 ? 'hoje' : dias === 1 ? 'amanhã' : `em ${String(dias)} dias`;
    return {
      tom: 'alerta',
      texto:
        acesso.situacao === 'TRIAL'
          ? `Seu teste termina ${quando}. Contrate um plano para não perder o acesso.`
          : `Sua assinatura vence ${quando}.`,
    };
  }
  return null;
}

export interface LinhaDeUso {
  rotulo: string;
  usado: number;
  limite: number | null;
  /** 0–100. `null` quando não há limite — sem teto não existe "quanto falta". */
  percentual: number | null;
  /** `true` a partir de 80% — é quando vale a pena avisar antes de esbarrar. */
  perto: boolean;
  estourado: boolean;
}

export function linhasDeUso(uso: UsoAtual, limites: LimitesContratados): LinhaDeUso[] {
  return [
    linha('Sociedades', uso.sociedades, limites.sociedades),
    linha('Usuários', uso.usuarios, limites.usuarios),
    linha('Atos neste mês', uso.atosNoMes, limites.atosPorMes),
  ];
}

function linha(rotulo: string, usado: number, limite: number | null): LinhaDeUso {
  if (limite === null) {
    return {
      rotulo,
      usado,
      limite: null,
      percentual: null,
      perto: false,
      estourado: false,
    };
  }
  // `limite` nunca é 0 por contrato (§3: "0 significaria nenhum"), mas dividir
  // por ele aqui produziria `Infinity` na barra em vez de erro — e uma barra
  // com largura `Infinity%` some da tela sem avisar.
  const percentual = limite > 0 ? Math.min(100, Math.round((usado / limite) * 100)) : 100;
  return {
    rotulo,
    usado,
    limite,
    percentual,
    perto: percentual >= 80 && usado < limite,
    estourado: usado >= limite,
  };
}
